import React, { useState, useMemo } from "react";
import { TrendingUp, BarChart3, Users, Scale } from "lucide-react";
import TopBar from "../components/layout/TopBar";
import SogliaSimulator from "../components/fiscal/SogliaSimulator";
import FatturatoSimulator from "../components/fiscal/FatturatoSimulator";
import ConfrontoDipendenteSimulator from "../components/fiscal/ConfrontoDipendenteSimulator";
import ForfettarioVsOrdinarioSimulator from "../components/simulations/ForfettarioVsOrdinarioSimulator";
import HelpTooltip from "../components/common/HelpTooltip";
import { useAppState } from "../hooks/useAppState";
import { getAliquotaINPS } from "../constants/aliquote";
import { getSogliePerAnno, calcolaAnniAttivita, getAliquotaSostitutiva } from "../utils/fiscalEngine";

const currentYear = new Date().getFullYear();

export default function SimulazioniPage() {
  const {
    user,
    darkMode,
    toggleDarkMode,
    invoices,
    fiscalSettings,
    isSynced,
  } = useAppState();

  const [selectedYear, setSelectedYear] = useState(currentYear);

  // Anni di attività e aliquota dell'anno SIMULATO: la pagina permette di
  // scegliere anni passati e l'anno prossimo (vedi `years` più sotto), quindi
  // devono seguire selectedYear come già fanno INPS, soglie e incassi.
  const anniAttivita = calcolaAnniAttivita(fiscalSettings.pivaYearStart, selectedYear);
  const isPrimoAnno = anniAttivita === 1;
  const aliquotaAutomatica = getAliquotaSostitutiva(fiscalSettings.pivaYearStart, selectedYear);
  const inpsRate = getAliquotaINPS(fiscalSettings.inpsType, selectedYear);
  // Soglie ragguagliate ai giorni di attività nel primo anno, piene negli anni successivi.
  const { soglia, sogliaExit } = getSogliePerAnno(fiscalSettings, selectedYear);

  const incassiAnno = useMemo(() => {
    const getYear = (dateStr) => dateStr ? new Date(dateStr).getFullYear() : 0;
    return invoices
      .filter((inv) => {
        if (!inv.paid) return false;
        const dateToUse = inv.paymentDate || inv.issueDate;
        return dateToUse && getYear(dateToUse) === selectedYear;
      })
      .reduce((sum, inv) => sum + (Number(inv.grossAmount) || 0), 0);
  }, [invoices, selectedYear]);

  const years = [selectedYear - 2, selectedYear - 1, selectedYear, selectedYear + 1];

  return (
    <div className={`min-h-screen transition-colors ${darkMode ? 'bg-neutral-950 text-white' : 'bg-neutral-50 text-neutral-900'}`}>
      <TopBar
        user={user}
        notificationsCount={0}
        darkMode={darkMode}
        toggleDarkMode={toggleDarkMode}
        onUploadLogo={() => {}}
        isSynced={isSynced}
      />

      <main className="max-w-5xl mx-auto px-4 sm:px-6 pb-24">
        {/* Header */}
        <div className="mt-6 mb-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className={`text-xl font-semibold ${darkMode ? 'text-white' : 'text-neutral-900'}`}>Simulazioni</h1>
              <p className={`mt-1 text-sm ${darkMode ? 'text-neutral-500' : 'text-neutral-500'}`}>
                Strumenti per pianificare e simulare scenari fiscali
              </p>
            </div>
            <select
              value={selectedYear}
              onChange={(e) => setSelectedYear(Number(e.target.value))}
              className={`px-3 py-2 rounded-lg border text-sm ${
                darkMode
                  ? 'border-neutral-700 bg-neutral-800 text-white'
                  : 'border-neutral-200 bg-white text-neutral-900'
              }`}
            >
              {years.map((y) => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="space-y-6">
          {/* Simulatore Soglia 85k/100k */}
          <div className={`rounded-2xl border p-6 ${darkMode ? 'border-neutral-800 bg-neutral-900' : 'border-neutral-200 bg-white'}`}>
            <div className="flex items-center gap-2 mb-4">
              <TrendingUp className={`w-5 h-5 ${darkMode ? 'text-emerald-400' : 'text-emerald-600'}`} />
              <h2 className={`text-sm font-semibold uppercase tracking-wide ${darkMode ? 'text-white' : 'text-neutral-900'}`}>
                Simulatore Soglia 85k / 100k
              </h2>
              <HelpTooltip text="Simula cosa succede avvicinandoti alla soglia di €85.000. Sopra €100.000, il passaggio all'ordinario è retroattivo." darkMode={darkMode} />
            </div>
            <SogliaSimulator
              incassiAnno={incassiAnno}
              profitabilityCoefficient={fiscalSettings.profitabilityCoefficient}
              aliquota={aliquotaAutomatica}
              inpsRate={inpsRate}
              soglia={soglia}
              sogliaExit={sogliaExit}
              darkMode={darkMode}
            />
          </div>

          {/* Simulatore Fatturato e Tasse */}
          <div className={`rounded-2xl border p-6 ${darkMode ? 'border-neutral-800 bg-neutral-900' : 'border-neutral-200 bg-white'}`}>
            <div className="flex items-center gap-2 mb-4">
              <BarChart3 className={`w-5 h-5 ${darkMode ? 'text-indigo-400' : 'text-indigo-600'}`} />
              <h2 className={`text-sm font-semibold uppercase tracking-wide ${darkMode ? 'text-white' : 'text-neutral-900'}`}>
                Simulatore Fatturato e Tasse
              </h2>
              <HelpTooltip text="Inserisci un fatturato ipotetico per vedere la stima di tasse e contributi." darkMode={darkMode} />
            </div>
            <FatturatoSimulator
              invoices={invoices}
              selectedYear={selectedYear}
              incassiAnno={incassiAnno}
              profitabilityCoefficient={fiscalSettings.profitabilityCoefficient}
              aliquota={aliquotaAutomatica}
              inpsRate={inpsRate}
              contributiPrecedenti={isPrimoAnno ? 0 : (fiscalSettings.contributiInpsPrecedenti || 0)}
              soglia={soglia}
              darkMode={darkMode}
            />
          </div>

          {/* Simulatore Forfettario vs Ordinario */}
          <div className={`rounded-2xl border p-6 ${darkMode ? 'border-neutral-800 bg-neutral-900' : 'border-neutral-200 bg-white'}`}>
            <div className="flex items-center gap-2 mb-4">
              <Scale className={`w-5 h-5 ${darkMode ? 'text-forfy-300' : 'text-forfy-600'}`} />
              <h2 className={`text-sm font-semibold uppercase tracking-wide ${darkMode ? 'text-white' : 'text-neutral-900'}`}>
                Forfettario vs Ordinario
              </h2>
              <HelpTooltip text="Confronta il netto stimato in regime forfettario con quello in regime ordinario, a parità di fatturato." darkMode={darkMode} />
            </div>
            <ForfettarioVsOrdinarioSimulator
              incassi={incassiAnno}
              coefficiente={fiscalSettings.profitabilityCoefficient}
              aliquotaImposta={aliquotaAutomatica}
              inpsRate={inpsRate}
              darkMode={darkMode}
            />
          </div>

          {/* Confronto Forfettario vs Dipendente */}
          <div className={`rounded-2xl border p-6 ${darkMode ? 'border-neutral-800 bg-neutral-900' : 'border-neutral-200 bg-white'}`}>
            <div className="flex items-center gap-2 mb-4">
              <Users className={`w-5 h-5 ${darkMode ? 'text-violet-400' : 'text-violet-600'}`} />
              <h2 className={`text-sm font-semibold uppercase tracking-wide ${darkMode ? 'text-white' : 'text-neutral-900'}`}>
                Confronto Forfettario vs Dipendente
              </h2>
              <HelpTooltip text="Confronta il tuo netto da forfettario con la RAL equivalente come dipendente. Utile per valutare offerte di lavoro." darkMode={darkMode} />
            </div>
            <ConfrontoDipendenteSimulator
              incassiAnno={incassiAnno}
              profitabilityCoefficient={fiscalSettings.profitabilityCoefficient}
              aliquota={aliquotaAutomatica}
              inpsRate={inpsRate}
              inpsType={fiscalSettings.inpsType}
              selectedYear={selectedYear}
              darkMode={darkMode}
            />
          </div>
        </div>
      </main>
    </div>
  );
}
